/**
 * Shot Latency API Client
 *
 * Fetches the latest shot latency data for a site from the backend.
 * Returns a discriminated union result rather than throwing for expected states (404).
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4
 */

import type { ShotLatencyResponse } from '../models/shotLatencyModels';

export interface ShotLatencyApiConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

export type ShotLatencyResult =
  | { readonly status: 'ok'; readonly data: ShotLatencyResponse }
  | { readonly status: 'no_data' }
  | { readonly status: 'error'; readonly message: string };

/**
 * Fetches the latest shot latency for a site.
 *
 * @param siteId - The site identifier
 * @param config - API host and token provider
 * @returns Discriminated union: ok with data, no_data on 404, error otherwise
 * @throws Error when access token is null
 */
export async function fetchShotLatency(
  siteId: string,
  config: ShotLatencyApiConfig
): Promise<ShotLatencyResult> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(
    `${config.apiHost}/site/${siteId}/shot-latency`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (response.status === 404) {
    return { status: 'no_data' };
  }

  if (!response.ok) {
    return { status: 'error', message: `API error: ${response.status}` };
  }

  const data: ShotLatencyResponse = await response.json();
  return { status: 'ok', data };
}
